import React, { useEffect, useMemo, useRef, useState } from "react";
import { applyPriceOverride, PRICE_OVERRIDE_MODE } from "../../../../helpers/estimateLinePricing";
import { formatCurrency } from "../../../../helpers/estimateTotals";
import { filterMaterialsCatalog } from "../../../../helpers/materialSearch";

/** $ = fixed unit price, % = plus or minus a percent of the unit price. */
export function YourPriceField({ mode, value, unitPrice, onChange, disabled }) {
  const active = value !== "" && value != null && (mode === PRICE_OVERRIDE_MODE.AMOUNT || mode === PRICE_OVERRIDE_MODE.PERCENT);
  const result = active ? applyPriceOverride(unitPrice, mode || PRICE_OVERRIDE_MODE.AMOUNT, value) : null;
  function setMode(next) {
    onChange({ mode: next, value });
  }
  return (
    <div className="eg-override">
      <div className="eg-override__switch" role="group" aria-label="Your price mode">
        <button type="button" aria-pressed={mode !== PRICE_OVERRIDE_MODE.PERCENT} disabled={disabled} onClick={() => setMode(PRICE_OVERRIDE_MODE.AMOUNT)}>$</button>
        <button type="button" aria-pressed={mode === PRICE_OVERRIDE_MODE.PERCENT} disabled={disabled} onClick={() => setMode(PRICE_OVERRIDE_MODE.PERCENT)}>%</button>
      </div>
      <input
        className="eg-input eg-input--num"
        type="number"
        step="any"
        aria-label="Your price"
        data-col="your-price"
        placeholder={mode === PRICE_OVERRIDE_MODE.PERCENT ? "20 or -10" : "0.00"}
        value={value ?? ""}
        disabled={disabled}
        onChange={(event) => onChange({ mode: mode || PRICE_OVERRIDE_MODE.AMOUNT, value: event.target.value })}
      />
      {result != null ? <span className="eg-override__result">= {formatCurrency(result)}</span> : null}
    </div>
  );
}

/** Per sq ft labour: one, several, or all areas in this feature. Empty selection means all. */
export function SurfacePicker({ areas, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = Array.isArray(value) ? value.filter(Boolean) : [];
  const all = selected.length === 0;
  const label = all
    ? "All surfaces"
    : areas
      .filter((area) => selected.includes(area.section.section_id))
      .map((area) => area.section.name || area.section.surface_type)
      .join(", ") || "Surfaces";

  function toggle(id) {
    if (all) {
      onChange([id]);
      return;
    }
    const next = selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id];
    onChange(next.length ? next : null);
  }

  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event) {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  return (
    <div className="eg-combo" ref={rootRef}>
      <button
        type="button"
        className="eg-input eg-combo__btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Surfaces"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
      </button>
      {open ? (
        <ul className="eg-combo__list" role="listbox" aria-label="Surfaces" aria-multiselectable="true">
          <li
            role="option"
            aria-selected={all}
            className={all ? "is-active" : ""}
            onMouseDown={(event) => {
              event.preventDefault();
              onChange(null);
              setOpen(false);
            }}
          >
            <span>All surfaces</span>
          </li>
          {areas.map((area) => {
            const id = area.section.section_id;
            const on = !all && selected.includes(id);
            return (
              <li
                key={id}
                role="option"
                aria-selected={on}
                className={on ? "is-active" : ""}
                onMouseDown={(event) => {
                  event.preventDefault();
                  toggle(id);
                }}
              >
                <span>{area.section.name || area.section.surface_type}</span>
                <span className="eg-combo__meta">{area.net.toFixed(1)} ft²</span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

/** Read-only catalog list opened from the materials or labour grid. */
export function CatalogDialog({ title, rows, onClose, showCost }) {
  const [query, setQuery] = useState("");
  const visible = useMemo(() => {
    const q = query.trim();
    const list = q ? filterMaterialsCatalog(rows, q, { limit: 50 }) : rows || [];
    return list.slice(0, 50);
  }, [rows, query]);
  useEffect(() => {
    function onKey(event) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="ed-catalog" role="presentation" onMouseDown={onClose}>
      <div className="ed-catalog__panel" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
        <header className="ed-catalog__head">
          <h3>{title}</h3>
          <button type="button" className="eg-link" onClick={onClose}>Close</button>
        </header>
        <input
          className="eg-input"
          aria-label="Search catalog"
          placeholder="Search…"
          value={query}
          autoFocus
          onChange={(event) => setQuery(event.target.value)}
        />
        <ul className="ed-catalog__list">
          {visible.length === 0 ? <li className="eg-combo__meta">Nothing matches.</li> : null}
          {visible.map((row) => {
            const client = row.sell_price != null && row.sell_price !== "" ? Number(row.sell_price) : Number(row.unit_cost) || 0;
            return (
              <li key={row.material_id}>
                <span>{row.material_name}</span>
                <span className="eg-combo__meta">
                  {row.unit_of_measure ? `${row.unit_of_measure} · ` : ""}
                  {formatCurrency(client)}
                  {showCost ? ` · our cost ${formatCurrency(row.unit_cost)}` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
